"use client";

import { useSuspenseQuery } from "@tanstack/react-query";
import { type ReactNode } from "react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#/components/ui/card";
import { FieldGroup } from "#/components/ui/field";
import { Skeleton } from "#/components/ui/skeleton";
import { playerProfileQueryOptions } from "#/lib/queries";

import { ProfileBooleanSetting } from "./-profile.booleanSetting";
import { DisplayNameField } from "./-profile.displayNameSetting";
import type { ProfileUserId } from "./-profile.types";

function SettingsCardShell({ children }: { children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Settings</CardTitle>
        <CardDescription>Manage your profile and game preferences.</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function SettingsCardSkeleton() {
  return (
    <SettingsCardShell>
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </SettingsCardShell>
  );
}

export function SettingsForm({ userId }: { userId: ProfileUserId }) {
  const { data: profile } = useSuspenseQuery(playerProfileQueryOptions(userId));

  return (
    <SettingsCardShell>
      <FieldGroup>
        <DisplayNameField userId={userId} initialDisplayName={profile.displayName} />
        <ProfileBooleanSetting
          userId={userId}
          setting="endTurnConfirmation"
          title="End Turn Confirmation"
          description="Show a warning if you try to end turn with moves left."
          initialEnabled={profile.endTurnConfirmation ?? true}
        />
        <ProfileBooleanSetting
          userId={userId}
          setting="fastAnimations"
          title="Fast Animations"
          description="Speed up animations during turn playback."
          initialEnabled={profile.fastAnimations ?? false}
        />
        <ProfileBooleanSetting
          userId={userId}
          setting="disableAiPlayback"
          title="Disable AI Playback"
          description="Skip the enemy turn playback after you end your turn."
          initialEnabled={profile.disableAiPlayback ?? false}
        />
      </FieldGroup>
    </SettingsCardShell>
  );
}
